import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeam } from '../hooks/useTeam';
import { storage } from '../lib/storage';
import Layout from '../components/Layout';

const APP_URL = 'https://lunchpick-cic.pages.dev';

export default function Home() {
  const navigate = useNavigate();
  const { createTeam, joinTeam, loading, error } = useTeam();

  const [tab, setTab] = useState('select');
  const [teamName, setTeamName] = useState('');
  const [createName, setCreateName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [joinName, setJoinName] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    const { teamId, memberName, soloMode } = storage.load();
    if (soloMode) navigate('/vote');
    else if (teamId && memberName) navigate('/vote');
  }, [navigate]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!teamName.trim() || !createName.trim()) return;
    setLocalError('');
    try {
      const team = await createTeam(teamName.trim(), createName.trim());
      setInviteLink(`${APP_URL}/join/${team.invite_code}`);
    } catch (err) {
      setLocalError(err.message || '팀 생성에 실패했습니다. 다시 시도해 주세요.');
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim() || !joinName.trim()) return;
    setLocalError('');
    try {
      await joinTeam(inviteCode.trim(), joinName.trim());
      navigate('/vote');
    } catch (err) {
      setLocalError(err.message || '팀 참여에 실패했습니다. 초대 코드를 확인해 주세요.');
    }
  };

  // ─── 솔로 모드 ──────────────────────────────────────────────────────────
  const handleSolo = () => {
    storage.save({ soloMode: true, memberName: '나', teamName: '솔로' });
    navigate('/vote');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('아래 링크를 복사하세요:', inviteLink);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({ title: '런치픽 팀 초대', text: '오늘 점심 같이 골라요!', url: inviteLink });
    } else {
      copyLink();
    }
  };

  const displayError = localError || error;

  // ─── 팀 생성 완료 ────────────────────────────────────────────────────────
  if (inviteLink) {
    return (
      <Layout>
        <div style={s.logoMark} aria-hidden="true">🍽️</div>
        <h1 style={s.logoText}>LunchPick</h1>

        <div className="card">
          <span style={s.badge}>🎉 팀 생성 완료</span>
          <h2 style={s.cardTitle}>팀원들을 초대하세요</h2>
          <p style={s.cardDesc}>아래 링크를 공유하면 팀원이 바로 참여할 수 있어요</p>

          <div style={s.linkBox}>
            <p style={s.linkText}>{inviteLink}</p>
          </div>

          <button className="btn-primary" onClick={handleShare} style={{ marginBottom: '0.5rem' }}>
            📤 팀원에게 공유하기
          </button>
          <button className="btn-outline" onClick={copyLink}>
            {copied ? '✓ 링크 복사됨!' : '🔗 링크 복사'}
          </button>

          <div style={s.divider} />

          <button className="btn-white" onClick={() => navigate('/vote')}>
            공유 완료 — 투표 화면으로 이동 →
          </button>
        </div>
      </Layout>
    );
  }

  // ─── 메인 화면 ───────────────────────────────────────────────────────────
  return (
    <Layout>
      <div style={s.logoMark} aria-hidden="true">🍽️</div>
      <h1 style={s.logoText}>LunchPick</h1>
      <p style={s.logoSub}>직장 동료들과 오늘 점심 메뉴를 쉽고 재미있게 결정하세요</p>

      {/* STEP 안내 */}
      {tab === 'select' && (
        <div style={s.steps} role="list" aria-label="사용 방법">
          {[
            { num: '1', icon: '👥', title: '팀 만들기',  desc: '팀 이름과 내 이름으로 시작' },
            { num: '2', icon: '🔗', title: '팀원 초대',  desc: '링크 공유 한 번으로 합류' },
            { num: '3', icon: '🗳️', title: '함께 투표', desc: 'OK·패스로 점심 결정' },
          ].map((st) => (
            <div key={st.num} style={s.stepRow} role="listitem">
              <div style={s.stepNum} aria-hidden="true">{st.num}</div>
              <span style={s.stepIcon} aria-hidden="true">{st.icon}</span>
              <div>
                <div style={s.stepTitle}>{st.title}</div>
                <div style={s.stepDesc}>{st.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 탭 선택 버튼 */}
      {tab === 'select' && (
        <div style={s.btnStack}>
          <button className="btn-primary" onClick={() => setTab('create')}>
            팀 만들기
          </button>
          <button className="btn-outline" onClick={() => setTab('join')}>
            팀 참여하기
          </button>
          <div style={s.soloSeparator}>
            <span style={s.soloLine} />
            <span style={s.soloOrText}>또는</span>
            <span style={s.soloLine} />
          </div>
          <button className="btn-solo" onClick={handleSolo}>
            🍴 혼자 결정하기
          </button>
        </div>
      )}

      {/* 팀 만들기 폼 */}
      {tab === 'create' && (
        <div className="card">
          <h2 style={s.cardTitle}>팀 만들기</h2>
          <form onSubmit={handleCreate} style={s.form} aria-label="팀 만들기 폼">
            <label style={s.srOnly} htmlFor="team-name-input">팀 이름</label>
            <input
              id="team-name-input"
              className="input"
              placeholder="팀 이름 (예: 개발팀)"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              required
              autoComplete="off"
            />
            <label style={s.srOnly} htmlFor="create-name-input">내 이름</label>
            <input
              id="create-name-input"
              className="input"
              placeholder="내 이름"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              required
              autoComplete="off"
            />
            {displayError && <p style={s.errMsg} role="alert">{displayError}</p>}
            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? '생성 중…' : '팀 만들기'}
            </button>
            <button className="btn-ghost" type="button" onClick={() => { setTab('select'); setLocalError(''); }}>
              ← 뒤로
            </button>
          </form>
        </div>
      )}

      {/* 팀 참여 폼 */}
      {tab === 'join' && (
        <div className="card">
          <h2 style={s.cardTitle}>팀 참여하기</h2>
          <form onSubmit={handleJoin} style={s.form} aria-label="팀 참여 폼">
            <label style={s.srOnly} htmlFor="invite-code-input">초대 코드</label>
            <input
              id="invite-code-input"
              className="input"
              placeholder="초대 코드 8자리"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              maxLength={8}
              required
              autoComplete="off"
            />
            <label style={s.srOnly} htmlFor="join-name-input">내 이름</label>
            <input
              id="join-name-input"
              className="input"
              placeholder="내 이름"
              value={joinName}
              onChange={(e) => setJoinName(e.target.value)}
              required
              autoComplete="off"
            />
            {displayError && <p style={s.errMsg} role="alert">{displayError}</p>}
            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? '참여 중…' : '참여하기'}
            </button>
            <button className="btn-ghost" type="button" onClick={() => { setTab('select'); setLocalError(''); }}>
              ← 뒤로
            </button>
          </form>
        </div>
      )}
    </Layout>
  );
}

// ─── 스타일 ──────────────────────────────────────────────────────────────────
const s = {
  /* 로고 */
  logoMark: { fontSize: '2.4rem', lineHeight: 1, marginBottom: '0.3rem' },
  logoText: {
    margin: '0 0 0.3rem', fontSize: '1.9rem', fontWeight: '800',
    color: '#ffffff', letterSpacing: '-0.02em',
  },
  logoSub: {
    margin: '0 0 1.5rem', fontSize: '0.88rem',
    color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.6,
  },

  /* STEP 리스트 */
  steps: {
    width: '100%', display: 'flex', flexDirection: 'column',
    gap: '0.4rem', marginBottom: '1.25rem',
  },
  stepRow: {
    display: 'flex', alignItems: 'center', gap: '0.7rem',
    background: 'var(--bg-card)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)', padding: '0.6rem 0.85rem',
  },
  stepNum: {
    width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
    background: 'var(--accent-gradient)',
    color: '#fff', fontSize: '0.72rem', fontWeight: '800',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  stepIcon: { fontSize: '1.1rem', flexShrink: 0 },
  stepTitle: { fontSize: '0.82rem', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '0.05rem' },
  stepDesc:  { fontSize: '0.7rem', color: 'var(--text-muted)' },

  /* 버튼 묶음 */
  btnStack: {
    width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem',
  },

  /* 뱃지 */
  badge: {
    display: 'inline-block', alignSelf: 'flex-start',
    background: 'var(--accent-gradient)',
    color: '#fff', fontSize: '0.75rem', fontWeight: '700',
    padding: '0.22rem 0.7rem', borderRadius: 'var(--radius-full)',
    marginBottom: '0.85rem', letterSpacing: '0.04em',
  },
  cardTitle: {
    margin: '0 0 0.35rem', fontSize: '1.35rem',
    fontWeight: '800', color: 'var(--text-primary)',
  },
  cardDesc: {
    margin: '0 0 1.25rem', fontSize: '0.87rem',
    color: 'var(--text-muted)', lineHeight: 1.55,
  },

  /* 초대 링크 박스 */
  linkBox: {
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '10px', padding: '0.7rem 1rem',
    marginBottom: '1rem',
  },
  linkText: {
    margin: 0, fontSize: '0.76rem',
    color: 'var(--text-muted)', wordBreak: 'break-all', lineHeight: 1.55,
  },

  /* 구분선 */
  divider: {
    height: '1px', background: 'var(--border-subtle)', margin: '1.1rem 0',
  },

  /* 솔로 구분선 */
  soloSeparator: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    margin: '0.3rem 0',
  },
  soloLine: {
    flex: 1, height: '1px', background: 'rgba(255,255,255,0.12)',
  },
  soloOrText: {
    fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: '500',
  },

  /* 폼 */
  form: { display: 'flex', flexDirection: 'column', gap: '0.7rem', marginTop: '1.1rem' },
  errMsg: { margin: 0, fontSize: '0.84rem', color: '#fca5a5' },

  /* 스크린리더 전용 (시각적으로 숨김) */
  srOnly: {
    position: 'absolute', width: '1px', height: '1px',
    padding: 0, margin: '-1px', overflow: 'hidden',
    clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0,
  },
};
